import update from 'immutability-helper'

import {
  INITIAL_CREATE_REDUCER,
  INITIAL_GET_REDUCER,
  INITIAL_GET_RESULTS_REDUCER,
  INITIAL_UPDATE_REDUCER,
  InitialGetReducer,
  InitialGetResultsReducer,
  InitialCreateReducer,
  InitialUpdateReducer
} from '../../constants/reducer'
import {
  // PLOP_APPEND_PATTERN_ANCHOR_ACTIONS_IMPORT
  CREATE_ASSET,
  CREATE_ASSET_SUCCESS,
  CREATE_ASSET_FAILURE,
  CLEAR_CREATE_ASSET,
  CREATE_ASSETS_BATCH,
  CREATE_ASSETS_BATCH_SUCCESS,
  CREATE_ASSETS_BATCH_FAILURE,
  CLEAR_CREATE_ASSETS_BATCH,
  CREATE_ASSET_GROUP,
  CREATE_ASSET_GROUP_SUCCESS,
  CREATE_ASSET_GROUP_FAILURE,
  CLEAR_CREATE_ASSET_GROUP,
  GET_ASSET_GROUPS,
  GET_ASSET_GROUPS_SUCCESS,
  GET_ASSET_GROUPS_FAILURE,
  CLEAR_ASSET_GROUPS,
  GET_ASSET_GROUP,
  GET_ASSET_GROUP_SUCCESS,
  GET_ASSET_GROUP_FAILURE,
  CLEAR_ASSET_GROUP,
  UPDATE_ASSET_GROUP,
  UPDATE_ASSET_GROUP_SUCCESS,
  UPDATE_ASSET_GROUP_FAILURE,
  CLEAR_UPDATE_ASSET_GROUP,
  APPROVE_INTERNAL_ASSET_GROUP,
  APPROVE_INTERNAL_ASSET_GROUP_SUCCESS,
  APPROVE_INTERNAL_ASSET_GROUP_FAILURE,
  CLEAR_APPROVE_INTERNAL_ASSET_GROUP,
  DISAPPROVE_INTERNAL_ASSET_GROUP,
  DISAPPROVE_INTERNAL_ASSET_GROUP_SUCCESS,
  DISAPPROVE_INTERNAL_ASSET_GROUP_FAILURE,
  CLEAR_DISAPPROVE_INTERNAL_ASSET_GROUP,
  CREATE_ASSET_GROUP_SIGNAL,
  CREATE_ASSET_GROUP_SIGNAL_SUCCESS,
  CREATE_ASSET_GROUP_SIGNAL_FAILURE,
  CLEAR_CREATE_ASSET_GROUP_SIGNAL,
  GET_ASSETS,
  GET_ASSETS_SUCCESS,
  GET_ASSETS_FAILURE,
  CLEAR_ASSETS
} from '../actions/assets'
import { deleteItemById, updateItem } from '../../helpers/reducerHelpers'
import { createReducer } from '@reduxjs/toolkit'
import {
  ApproveInternalAssetGroupFailurePayload,
  ApproveInternalAssetGroupPayload,
  ApproveInternalAssetGroupSuccessPayload,
  CreateAssetFailurePayload,
  CreateAssetGroupFailurePayload,
  CreateAssetGroupSignalFailurePayload,
  CreateAssetGroupSuccessPayload,
  CreateAssetsBatchFailurePayload,
  CreateAssetsBatchSuccessPayload,
  DisapproveInternalAssetGroupFailurePayload,
  DisapproveInternalAssetGroupPayload,
  DisapproveInternalAssetGroupSuccessPayload,
  GetAssetGroupFailurePayload,
  GetAssetGroupsFailurePayload,
  GetAssetGroupsSuccessPayload,
  GetAssetGroupSuccessPayload,
  GetAssetsFailurePayload,
  GetAssetsSuccessPayload,
  UpdateAssetGroupFailurePayload,
  UpdateAssetGroupPayload,
  UpdateAssetGroupSuccessPayload
} from '../types/assets'

export type AssetsInitialStateType = {
  assetGroups: InitialGetResultsReducer
  assetGroup: InitialGetReducer
  assets: InitialGetReducer & {
    data: {
      results: any[]
      next_page_token: string
    }
  }
  createdAsset: InitialCreateReducer
  createdAssets: InitialCreateReducer
  createdAssetGroup: InitialCreateReducer
  updatedAssetGroup: InitialUpdateReducer
  approveInternalAssetGroup: {
    [id: string | number]: {
      isLoading: boolean
      wasApproved: boolean
      error: object | null
    }
  }
  disapproveInternalAssetGroup: {
    [id: string | number]: {
      isLoading: boolean
      wasDisapproved: boolean
      error: object | null
    }
  }
  createAssetGroupSignal: InitialCreateReducer
}

const initialState: AssetsInitialStateType = {
  // PLOP_APPEND_PATTERN_ANCHOR_INITIAL_REDUCER
  assetGroups: INITIAL_GET_RESULTS_REDUCER,
  assetGroup: INITIAL_GET_REDUCER,
  assets: {
    ...INITIAL_GET_REDUCER,
    data: {
      results: [],
      next_page_token: ''
    }
  },
  createdAsset: INITIAL_CREATE_REDUCER,
  createdAssets: INITIAL_CREATE_REDUCER,
  createdAssetGroup: INITIAL_CREATE_REDUCER,
  updatedAssetGroup: INITIAL_UPDATE_REDUCER,
  approveInternalAssetGroup: {},
  disapproveInternalAssetGroup: {},
  createAssetGroupSignal: INITIAL_CREATE_REDUCER
}

export default createReducer(initialState, builder => {
  builder
    // PLOP_APPEND_PATTERN_ANCHOR_REDUCERS
    // create asset
    .addCase(CREATE_ASSET, state => {
      return update(state, {
        createdAsset: {
          $merge: {
            isLoading: true
          }
        }
      })
    })
    .addCase(CREATE_ASSET_SUCCESS, state => {
      return update(state, {
        createdAsset: {
          $merge: {
            isLoading: false,
            wasCreated: true
          }
        }
      })
    })
    .addCase(CREATE_ASSET_FAILURE, (state, action: CreateAssetFailurePayload) => {
      return update(state, {
        createdAsset: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    })
    .addCase(CLEAR_CREATE_ASSET, state => {
      return update(state, {
        createdAsset: { $set: initialState.createdAsset }
      })
    })

    // get assets
    .addCase(GET_ASSETS, state => {
      return update(state, {
        assets: {
          $merge: {
            isLoading: true
          }
        }
      })
    })
    .addCase(GET_ASSETS_SUCCESS, (state, action: GetAssetsSuccessPayload) => {
      return update(state, {
        assets: {
          data: {
            results: {
              $push: action.assetsData.results
            },
            next_page_token: {
              $set: action.assetsData.next_page_token
            }
          },
          $merge: {
            isLoading: false,
            wasLoaded: true
          }
        }
      })
    })
    .addCase(GET_ASSETS_FAILURE, (state, action: GetAssetsFailurePayload) => {
      return update(state, {
        assets: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    })
    .addCase(CLEAR_ASSETS, state => {
      return update(state, {
        assets: { $set: initialState.assets }
      })
    })

    // create multiple assets
    .addCase(CREATE_ASSETS_BATCH, state => {
      return update(state, {
        createdAssets: {
          $merge: {
            isLoading: true
          }
        }
      })
    })
    .addCase(CREATE_ASSETS_BATCH_SUCCESS, (state, action: CreateAssetsBatchSuccessPayload) => {
      return update(state, {
        createdAssets: {
          $merge: {
            isLoading: false,
            wasCreated: true,
            data: action.assetsData
          }
        }
      })
    })
    .addCase(CREATE_ASSETS_BATCH_FAILURE, (state, action: CreateAssetsBatchFailurePayload) => {
      return update(state, {
        createdAssets: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    })
    .addCase(CLEAR_CREATE_ASSETS_BATCH, state => {
      return update(state, {
        createdAssets: { $set: initialState.createdAssets }
      })
    })

    // create asset group
    .addCase(CREATE_ASSET_GROUP, state => {
      return update(state, {
        createdAssetGroup: {
          $merge: {
            isLoading: true
          }
        }
      })
    })
    .addCase(CREATE_ASSET_GROUP_SUCCESS, (state, action: CreateAssetGroupSuccessPayload) => {
      return update(state, {
        createdAssetGroup: {
          $merge: {
            isLoading: false,
            wasCreated: true,
            data: action.assetGroupData
          }
        },
        assetGroups: {
          results: {
            $unshift: [action.assetGroupData]
          }
        }
      })
    })
    .addCase(CREATE_ASSET_GROUP_FAILURE, (state, action: CreateAssetGroupFailurePayload) => {
      return update(state, {
        createdAssetGroup: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    })
    .addCase(CLEAR_CREATE_ASSET_GROUP, state => {
      return update(state, {
        createdAssetGroup: { $set: initialState.createdAssetGroup }
      })
    })

    // get assetGroups
    .addCase(GET_ASSET_GROUPS, state => {
      return update(state, {
        assetGroups: {
          $merge: {
            isLoading: true
          }
        }
      })
    })
    .addCase(GET_ASSET_GROUPS_SUCCESS, (state, action: GetAssetGroupsSuccessPayload) => {
      return update(state, {
        assetGroups: {
          $merge: {
            isLoading: false,
            wasLoaded: true,
            results: action.assetGroupsData.results
          }
        }
      })
    })
    .addCase(GET_ASSET_GROUPS_FAILURE, (state, action: GetAssetGroupsFailurePayload) => {
      return update(state, {
        assetGroups: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    })
    .addCase(CLEAR_ASSET_GROUPS, state => {
      return update(state, {
        assetGroups: { $set: initialState.assetGroups }
      })
    })

    // get assetGroup
    .addCase(GET_ASSET_GROUP, state => {
      return update(state, {
        assetGroup: {
          $merge: {
            isLoading: true
          }
        }
      })
    })
    .addCase(GET_ASSET_GROUP_SUCCESS, (state, action: GetAssetGroupSuccessPayload) => {
      return update(state, {
        assetGroup: {
          $merge: {
            isLoading: false,
            wasLoaded: true,
            data: action.assetGroupData
          }
        }
      })
    })
    .addCase(GET_ASSET_GROUP_FAILURE, (state, action: GetAssetGroupFailurePayload) => {
      return update(state, {
        assetGroup: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    })
    .addCase(CLEAR_ASSET_GROUP, state => {
      return update(state, {
        assetGroup: { $set: initialState.assetGroup }
      })
    })

    // update asset group
    .addCase(UPDATE_ASSET_GROUP, (state, action: UpdateAssetGroupPayload) => {
      return update(state, {
        updatedAssetGroup: {
          $merge: {
            isLoading: true,
            id: action.id
          }
        }
      })
    })
    .addCase(UPDATE_ASSET_GROUP_SUCCESS, (state, action: UpdateAssetGroupSuccessPayload) => {
      return update(state, {
        updatedAssetGroup: {
          $merge: {
            isLoading: false,
            wasUpdated: true,
            data: action.assetGroupData
          }
        },
        assetGroups: {
          results: { $apply: (items: object[]) => updateItem(items, action.assetGroupData) }
        },
        assetGroup: {
          data: { $merge: action.assetGroupData }
        }
      })
    })
    .addCase(UPDATE_ASSET_GROUP_FAILURE, (state, action: UpdateAssetGroupFailurePayload) => {
      return update(state, {
        updatedAssetGroup: {
          $merge: {
            ...initialState.updatedAssetGroup,
            error: action.error
          }
        }
      })
    })
    .addCase(CLEAR_UPDATE_ASSET_GROUP, state => {
      return update(state, {
        updatedAssetGroup: { $set: initialState.updatedAssetGroup }
      })
    })

    // Approve internal asset group
    .addCase(APPROVE_INTERNAL_ASSET_GROUP, (state, action: ApproveInternalAssetGroupPayload) => {
      return update(state, {
        approveInternalAssetGroup: {
          $merge: {
            [action.id]: {
              isLoading: true
            }
          }
        }
      })
    })
    .addCase(APPROVE_INTERNAL_ASSET_GROUP_SUCCESS, (state, action: ApproveInternalAssetGroupSuccessPayload) => {
      return update(state, {
        approveInternalAssetGroup: {
          $merge: {
            [action.id]: {
              isLoading: false,
              wasApproved: true
            }
          }
        },
        assetGroups: {
          results: { $apply: (items: object[]) => updateItem(items, action.data, action.id) }
        }
      })
    })
    .addCase(APPROVE_INTERNAL_ASSET_GROUP_FAILURE, (state, action: ApproveInternalAssetGroupFailurePayload) => {
      return update(state, {
        approveInternalAssetGroup: {
          $merge: {
            [action.id]: {
              isLoading: false,
              error: action.error
            }
          }
        }
      })
    })
    .addCase(CLEAR_APPROVE_INTERNAL_ASSET_GROUP, (state, action) => {
      return update(state, {
        // approveInternalAssetGroup: { $set: initialState.approveInternalAssetGroup }
        approveInternalAssetGroup: {
          $merge: {
            [action.id]: null
          }
        }
      })
    })

    // disapprove asset group
    .addCase(DISAPPROVE_INTERNAL_ASSET_GROUP, (state, action: DisapproveInternalAssetGroupPayload) => {
      return update(state, {
        disapproveInternalAssetGroup: {
          $merge: {
            [action.id]: {
              isLoading: true
            }
          }
        }
      })
    })
    .addCase(DISAPPROVE_INTERNAL_ASSET_GROUP_SUCCESS, (state, action: DisapproveInternalAssetGroupSuccessPayload) => {
      return update(state, {
        assetGroups: {
          results: {
            $apply: (items: object[]) => deleteItemById(items, action.id)
          }
        },
        disapproveInternalAssetGroup: {
          $merge: {
            [action.id]: {
              isLoading: false,
              wasDisapproved: true
            }
          }
        }
      })
    })
    .addCase(DISAPPROVE_INTERNAL_ASSET_GROUP_FAILURE, (state, action: DisapproveInternalAssetGroupFailurePayload) => {
      return update(state, {
        disapproveInternalAssetGroup: {
          $merge: {
            [action.id]: {
              isLoading: false,
              error: action.error
            }
          }
        }
      })
    })
    .addCase(CLEAR_DISAPPROVE_INTERNAL_ASSET_GROUP, (state, action) => {
      return update(state, {
        disapproveInternalAssetGroup: {
          $merge: {
            [action.id]: null
          }
        }
      })
    })

    // create asset group signal
    .addCase(CREATE_ASSET_GROUP_SIGNAL, state => {
      return update(state, {
        createAssetGroupSignal: {
          $merge: {
            isLoading: true
          }
        }
      })
    })
    .addCase(CREATE_ASSET_GROUP_SIGNAL_SUCCESS, state => {
      return update(state, {
        createAssetGroupSignal: {
          $merge: {
            isLoading: false,
            wasCreated: true
          }
        }
      })
    })
    .addCase(CREATE_ASSET_GROUP_SIGNAL_FAILURE, (state, action: CreateAssetGroupSignalFailurePayload) => {
      return update(state, {
        createAssetGroupSignal: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    })
    .addCase(CLEAR_CREATE_ASSET_GROUP_SIGNAL, state => {
      return update(state, {
        createAssetGroupSignal: { $set: initialState.createAssetGroupSignal }
      })
    })
})
